import cloneDeep from 'lodash/cloneDeep';
import {
  DraftTopExpression,
  INSTANCE_SPLIT_SYMBOL,
  OperatorType,
  QueryBuilderModel,
  Variable,
  addDraftFields,
} from 'vet-bones/utils/queryBuilder';

import {
  ModelRule,
  ModelRuleType,
} from 'src/containers/ProjectDiagram/types/data';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import { RulesInfo } from 'src/containers/PublishProjectDrawer/types';
import {
  RDFS_LABEL_IRI,
  RULE_CONTENT_IRI,
  RULE_SPARQL_RULE_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { buildIfClause } from 'src/utils/stringify/rules/buildIfClause';
import { buildThenClause } from 'src/utils/stringify/rules/buildThenClause';
import { getRuleVariable } from 'src/utils/stringify/rules/getRuleVariable';

const buildRule = ({
  ruleIri,
  ruleName,
  getVariable,
  variablesById,
  instancesByIds,
  ifTopExpression,
  thenTopExpression,
}: {
  ruleIri: string;
  ruleName: string;
  getVariable: (variable: string, id?: string) => string;
  variablesById: Record<string, string>;
  instancesByIds: Array<Record<string, string>>;
  ifTopExpression: DraftTopExpression;
  thenTopExpression: DraftTopExpression;
}) => {
  const ruleContent = `${buildIfClause({
    topExpression: ifTopExpression,
    getVariable,
    variablesById,
    instancesByIds,
  })}\n${buildThenClause({
    topExpression: thenTopExpression,
    getVariable,
    instancesByIds,
  })}`;

  const ruleSparql = `${ruleIri} a <${RULE_SPARQL_RULE_IRI}> ;
  <${RDFS_LABEL_IRI}> "${escapeDoubleQuotes(ruleName)}" ;
  <${RULE_CONTENT_IRI}> """${
    escapeDoubleQuotes(ruleContent) // must use triple quotes for new line char
  }""" .
`;

  return {
    ruleContent,
    ruleSparql,
  };
};

const buildTextRule = ({
  ruleContent,
  ruleIri,
  ruleName,
}: {
  ruleContent: string;
  ruleIri: string;
  ruleName: string;
}) => {
  const ruleSparql = `${ruleIri} a <${RULE_SPARQL_RULE_IRI}> ;
  <${RDFS_LABEL_IRI}> "${escapeDoubleQuotes(ruleName)}" ;
  <${RULE_CONTENT_IRI}> """${
    escapeDoubleQuotes(ruleContent) // must use triple quotes for new line char
  }""" .
`;

  return {
    ruleContent,
    ruleSparql,
  };
};

export const getRuleIriFromId = (id: string): string => {
  let ruleIri: string;
  if (id.startsWith('_:')) {
    ruleIri = id;
  } else if (id.includes(':')) {
    ruleIri = `<${id}>`;
  } else {
    ruleIri = `_:${id}`;
  }
  return ruleIri;
};

export const stringifyTextRule = (rule: ModelRule) => {
  const ruleIri = getRuleIriFromId(rule.id);
  const { ruleContent, ruleSparql } = buildTextRule({
    ruleContent: rule.text || '',
    ruleIri,
    ruleName: rule.name,
  });
  return { ruleContents: [ruleContent], ruleSparql };
};

export const stringifyQueryBuilderRule = (
  rule: Pick<ModelRule, 'id' | 'name'>,
  ifTopExpression: DraftTopExpression,
  thenTopExpression: DraftTopExpression
): {
  ruleContents: string[];
  ruleSparql: string;
} => {
  const { getVariable, variablesById } = getRuleVariable();
  const ruleIri = getRuleIriFromId(rule.id);

  // we only allow IN operator in if clause
  const allInstancesById = ifTopExpression.expressions.reduce<
    Record<string, string[]>
  >((acc, subExpression) => {
    subExpression.expressions.forEach((expression) => {
      expression.filters.forEach((filter) => {
        if (
          !filter.subject ||
          !filter.operator ||
          typeof filter.value !== 'string'
        ) {
          return;
        }

        if (filter.operator.operatorType === OperatorType.INSTANCE) {
          const iris = filter.value
            .split(INSTANCE_SPLIT_SYMBOL)
            .filter(Boolean)
            .map((iri) => iri.replace(/`/g, ''));

          acc[filter.subject.id] = iris;
        }
      });
    });
    return acc;
  }, {});

  // we do not want to allow multiple instances in the then clause, so we split them out
  const splitThenClauseIds = thenTopExpression.expressions.reduce<Set<string>>(
    (acc, subExpression) => {
      const addInstanceId = ({ id }: Variable) => {
        if (allInstancesById[id]?.length > 1) {
          acc.add(id);
        }
      };

      subExpression.expressions.forEach((expression) => {
        expression.filters.forEach((filter) => {
          if (!filter.subject || !filter.operator) {
            return;
          }

          addInstanceId(filter.subject);

          if (filter.value && typeof filter.value !== 'string') {
            addInstanceId(filter.value);
          }
        });
      });
      return acc;
    },
    new Set()
  );

  // if there are no instances in the then clause with more than one value,
  // use the initial rule iri and name directly
  if (!splitThenClauseIds.size) {
    const instancesByIds: Array<Record<string, string>> = [{}];
    Object.entries(allInstancesById).forEach(([id, iris]) => {
      const recordsToCopy = [...instancesByIds];
      iris.forEach((iri, idx) => {
        if (!idx) {
          instancesByIds.forEach((instancesById) => {
            // eslint-disable-next-line no-param-reassign
            instancesById[id] = iri;
          });
        } else {
          recordsToCopy.forEach((record) => {
            instancesByIds.push({ ...record, [id]: iri });
          });
        }
      });
    });

    const { ruleContent, ruleSparql } = buildRule({
      ruleIri,
      ruleName: rule.name,
      ifTopExpression,
      thenTopExpression,
      getVariable,
      variablesById,
      instancesByIds,
    });
    return { ruleContents: [ruleContent], ruleSparql };
  }

  // otherwise, split these up into multiple rules and initialize a base record
  // of instancesById for each combination of single instance values
  const baseInstancesByIds: Array<Record<string, string>> = [{}];
  splitThenClauseIds.forEach((id) => {
    const recordsToCopy = [...baseInstancesByIds];
    allInstancesById[id].forEach((iri, idx) => {
      if (!idx) {
        baseInstancesByIds.forEach((instancesById) => {
          // eslint-disable-next-line no-param-reassign
          instancesById[id] = iri;
        });
      } else {
        recordsToCopy.forEach((record) => {
          baseInstancesByIds.push({ ...record, [id]: iri });
        });
      }
    });
  });

  const rules = baseInstancesByIds.map((baseInstancesById, idx) => {
    const instancesByIds: Array<Record<string, string>> = [
      { ...baseInstancesById },
    ];
    Object.entries(allInstancesById).forEach(([id, iris]) => {
      if (baseInstancesById[id]) {
        return;
      }
      const recordsToCopy = [...instancesByIds];
      iris.forEach((iri, idx) => {
        if (!idx) {
          instancesByIds.forEach((instancesById) => {
            // eslint-disable-next-line no-param-reassign
            instancesById[id] = iri;
          });
        } else {
          recordsToCopy.forEach((record) => {
            instancesByIds.push({ ...record, [id]: iri });
          });
        }
      });
    });

    // remove the multiple instance values from the if clause data that is saved
    const newIfTopExpression = cloneDeep(ifTopExpression);
    newIfTopExpression.expressions.forEach((subExpression) => {
      subExpression.expressions.forEach((expression) => {
        expression.filters.forEach((filter) => {
          if (!filter.subject || !filter.operator) {
            return;
          }

          if (filter.operator.operatorType === OperatorType.INSTANCE) {
            const subjectId = filter.subject.id;
            const singleValue = baseInstancesById[subjectId];
            if (singleValue) {
              // eslint-disable-next-line no-param-reassign
              filter.value = `\`${singleValue}\``;
            }
          }
        });
      });
    });

    return buildRule({
      ruleIri: `${ruleIri}_${idx + 1}`,
      ruleName: `${rule.name} (${idx + 1})`,
      ifTopExpression: newIfTopExpression,
      thenTopExpression,
      getVariable,
      variablesById,
      instancesByIds,
    });
  });

  return {
    ruleContents: rules.map(({ ruleContent }) => ruleContent),
    ruleSparql: rules.map(({ ruleSparql }) => ruleSparql).join('\n\n'),
  };
};

export const stringifyRule = (
  rule: ModelRule
): {
  ruleContents: string[];
  ruleSparql: string;
} => {
  if (rule.type === ModelRuleType.TEXT || !rule.ifClause || !rule.thenClause) {
    return stringifyTextRule(rule);
  }

  return stringifyQueryBuilderRule(
    rule,
    addDraftFields(rule.ifClause.data.query),
    addDraftFields(rule.thenClause.data.query)
  );
};

export const stringifyRules = (
  model: QueryBuilderModel,
  ruleInfo: RulesInfo
) => {
  const validRules = Object.values(ruleInfo).filter((rule) =>
    isRuleValid(rule, model)
  );

  return validRules.map((rule) => stringifyRule(rule).ruleSparql).join('\n\n');
};
